import React, { useMemo, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import styled from 'styled-components/macro'
import {
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.styles'
import {
  DropDownMenuWithNesting,
  FilterGroupHeading,
  FilterOptionContainer,
  FilterItemsContainer,
  NoFilterMatches,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.styles'
import { DropdownItemType } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'
import {
  FILTER_MENU_MAX_WIDTH,
  FILTER_MENU_MIN_WIDTH,
  FilterAccordionBody,
  FilterAccordionChevron,
  FilterAccordionHeading,
  FilterAccordionToggle,
  FilterOptionLabel,
  filterOptionsBySearch,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterAccordion'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { CountChip } from 'src/components/Reusable/Chips/BasicChip'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput/DropdownSearchInput'

const GroupRow = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}

  .form-check {
    flex: 1;
    min-width: 0;
  }
`

const OptionRow = styled.div`
  padding: 2px 0;
`

/**
 * How options with a nestedFilter show their children:
 * - headings: a heading over its options (e.g. label categories)
 * - accordion: a row that opens its options below it (e.g. "More filters")
 * - selectableAccordion: an option itself, which also opens its children below it
 *   (e.g. a framework over its requirements)
 */
export type LocalFilterGroupStyle =
  | 'headings'
  | 'accordion'
  | 'selectableAccordion'

interface LocalFilterDropdownProps {
  filterName: string
  filterValues: DropdownItemType[]
  isLoading?: boolean
  selectedValues: string[]
  onChangeSelectedValues: (values: string[]) => void
  searchPlaceholder?: string
  noMatchesText?: string
  groupStyle?: LocalFilterGroupStyle
}

/**
 * A filter dropdown that keeps its selection in the caller's state rather than the page
 * URL, for filters inside a modal. It shares its search and accordion pieces with the
 * index pages' FilterDropdown.
 */
export const LocalFilterDropdown: React.FC<LocalFilterDropdownProps> = ({
  filterName,
  filterValues,
  isLoading,
  selectedValues,
  onChangeSelectedValues,
  searchPlaceholder = 'Search...',
  noMatchesText = 'No matches found.',
  groupStyle = 'headings',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // Accordion groups start closed; a search opens every group with a match.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const isSearching = !!searchQuery.trim()

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const visibleValues = useMemo(
    () => filterOptionsBySearch(filterValues, searchQuery),
    [filterValues, searchQuery],
  )

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow)
    if (!nextShow) {
      setSearchQuery('')
    }
  }

  const toggleValue = (value: string) =>
    onChangeSelectedValues(
      selectedSet.has(value)
        ? selectedValues.filter((id) => id !== value)
        : [...selectedValues, value],
    )

  const toggleExpanded = (value: string) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      next.has(value) ? next.delete(value) : next.add(value)
      return next
    })

  const renderOption = (option: DropdownItemType) => (
    <OptionRow key={option.value} onClick={(e) => e.stopPropagation()}>
      <CheckBoxInput
        id={`${filterName}-${option.value}`}
        label={<FilterOptionLabel>{option.label}</FilterOptionLabel>}
        checked={selectedSet.has(option.value)}
        onChange={() => toggleValue(option.value)}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      />
    </OptionRow>
  )

  const renderGroup = (group: DropdownItemType) => {
    const children = group.nestedFilter?.filterValues ?? []
    if (groupStyle === 'headings') {
      return (
        <FilterOptionContainer key={group.value} style={{ marginBottom: 12 }}>
          <FilterGroupHeading>{group.label}</FilterGroupHeading>
          {children.map(renderOption)}
        </FilterOptionContainer>
      )
    }
    const isGroupOpen = isSearching || expanded.has(group.value)
    const selectedChildren = children.filter((child) =>
      selectedSet.has(child.value),
    ).length
    const label = typeof group.label === 'string' ? group.label : filterName
    return (
      <FilterOptionContainer key={group.value} style={{ marginBottom: 4 }}>
        {groupStyle === 'selectableAccordion' ? (
          <GroupRow onClick={(e) => e.stopPropagation()}>
            <CheckBoxInput
              id={`${filterName}-${group.value}`}
              label={<FilterOptionLabel>{group.label}</FilterOptionLabel>}
              checked={selectedSet.has(group.value)}
              onChange={() => toggleValue(group.value)}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
            />
            {!isGroupOpen && selectedChildren > 0 && (
              <CountChip>{selectedChildren}</CountChip>
            )}
            {!isSearching && (
              <FilterAccordionToggle
                isOpen={isGroupOpen}
                onToggle={() => toggleExpanded(group.value)}
                label={label}
              />
            )}
          </GroupRow>
        ) : (
          <GroupRow
            $clickable={!isSearching}
            role="button"
            aria-expanded={isGroupOpen}
            onClick={(e) => {
              e.stopPropagation()
              if (!isSearching) toggleExpanded(group.value)
            }}
          >
            <FilterAccordionHeading>{group.label}</FilterAccordionHeading>
            {selectedChildren > 0 && <CountChip>{selectedChildren}</CountChip>}
            <span style={{ padding: '0 8px' }}>
              <FilterAccordionChevron isOpen={isGroupOpen} />
            </span>
          </GroupRow>
        )}
        {isGroupOpen && (
          <FilterAccordionBody>
            {children.map(renderOption)}
          </FilterAccordionBody>
        )}
      </FilterOptionContainer>
    )
  }

  return (
    <div>
      <Dropdown show={isOpen} onToggle={handleToggle} autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          type="button"
          id={`${filterName}-local-dropdown`}
          $hasSelection={selectedValues.length > 0}
        >
          <FilterDropdownToggleContent>
            <p>{filterName}</p>
            {selectedValues.length > 0 && (
              <CountChip ml="xxs">{selectedValues.length}</CountChip>
            )}
          </FilterDropdownToggleContent>
        </Dropdown.Toggle>
        {isLoading ? (
          <Dropdown.Menu rootCloseEvent="mousedown">
            <MenuPlaceholder height={108} />
          </Dropdown.Menu>
        ) : (
          <DropDownMenuWithNesting
            $hasNested={false}
            rootCloseEvent="mousedown"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            style={{
              zIndex: 1055,
              maxWidth: FILTER_MENU_MAX_WIDTH,
              minWidth: FILTER_MENU_MIN_WIDTH,
              width: 'max-content',
            }}
          >
            <DropdownSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={searchPlaceholder}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            <FilterItemsContainer
              style={{
                padding: '8px 12px',
                maxHeight: '280px',
                overflowY: 'auto',
              }}
            >
              {visibleValues.length === 0 ? (
                <NoFilterMatches>{noMatchesText}</NoFilterMatches>
              ) : (
                visibleValues.map((option) =>
                  option.nestedFilter
                    ? renderGroup(option)
                    : renderOption(option),
                )
              )}
            </FilterItemsContainer>
          </DropDownMenuWithNesting>
        )}
      </Dropdown>
    </div>
  )
}
